import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import 'tailwindcss/tailwind.css';

import { Divider } from '@mui/material';
import { HeroSection } from './components/HeroSection';
import { FeaturesSection } from './components/FeaturesSection';
import { FAQSection } from './components/FAQSection';
import { ReelsSection } from './ReelsSection';
import { MidPageCTA } from './components/MidPageCTA';
import Footer from '../../footer';

// Componente para animar la transición entre secciones
const ScrollAnimation = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 60,
        damping: 20,
        delay: delay
      }
    }}
    viewport={{ once: true, amount: 0.25 }}
  >
    {children}
  </motion.div>
);

// Componente animado para la línea divisoria
const AnimatedDivider = () => (
  <motion.div
    initial={{ scaleX: 0 }}
    whileInView={{ 
      scaleX: 1,
      transition: { 
        duration: 1.2, 
        ease: "easeInOut" 
      }
    }}
    viewport={{ once: true }}
    className="w-full"
  >
    <Divider className="bg-zinc-700" style={{ height: 2 }} />
  </motion.div>
);

// Efecto de scroll suave para toda la página
const SmoothScrollProvider = ({ children }) => {
  useEffect(() => {
    // Configura desplazamiento suave para toda la página
    document.documentElement.style.scrollBehavior = 'smooth';
    
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return children;
};

const Landing = () => {
  // Para efectos de paralaje mientras se hace scroll
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <SmoothScrollProvider>
      <motion.div 
        className="relative overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ 
          opacity: 1,
          transition: { duration: 0.8 }
        }}
      >
        
        {/* Efecto de partículas de fondo que se mueven al hacer scroll */}
        <motion.div
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            background: 'radial-gradient(circle, rgba(120, 0, 240, 0.03) 0%, rgba(0, 0, 0, 0) 70%)',
            transform: `translateY(${scrollY * 0.2}px)`
          }}
        />
        
        {/* Hero Section con entrada animada */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: 1,
            transition: { duration: 1.2 }
          }}
        >
          <HeroSection />
        </motion.div>
        
        <AnimatedDivider />
        
        {/* Features Section con animación de entrada */}
        <div id="features">
          <ScrollAnimation delay={0.2}>
            <FeaturesSection />
          </ScrollAnimation>
        </div>
        
        <AnimatedDivider />
        
        {/* Reels Section con animación de entrada */}
        <div id="reels">
          <ScrollAnimation delay={0.2}>
            <ReelsSection />
          </ScrollAnimation>
        </div>
        
        <AnimatedDivider />
        
        {/* Call To Action intermedio */}
        <ScrollAnimation delay={0.1}>
          <MidPageCTA />
        </ScrollAnimation>
        
        <AnimatedDivider />
        
        {/* FAQ Section con animación de entrada */}
        <div id="faq">
          <ScrollAnimation delay={0.1}>
            <FAQSection />
          </ScrollAnimation>
        </div>
        
        {/* Indicador de scroll animado */}
        <motion.div 
          className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-50 w-10 h-10 md:w-12 md:h-12 rounded-full bg-red-600 flex items-center justify-center cursor-pointer opacity-70 hover:opacity-100"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ 
            opacity: scrollY > 300 ? 0.7 : 0,
            scale: scrollY > 300 ? 1 : 0.8,
            transition: { duration: 0.3 }
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </motion.div>
      </motion.div>
    </SmoothScrollProvider>
  );
};

export default Landing;